import { SupabaseInstance } from "@/integrations/supabase";

type UpdatePasswordFlowParams = {
  access_token: string;
  refresh_token: string;
};

/**
 * Service class for authentication-related operations.
 *
 * Handles flows that require direct interaction with Supabase edge functions
 * and session management, such as password updates via secure tokens.
 */
class AuthServices {
  /**
   * Initiates the update-password flow by invoking the `update-password` edge function
   * and refreshing the user session using the provided tokens.
   *
   * @param {UpdatePasswordFlowParams} params - The access and refresh tokens from the recovery link.
   * @returns The updated password response, the refreshed session, and any errors encountered.
   */
  async setUpdatePasswordFlow(
    { access_token, refresh_token }: UpdatePasswordFlowParams,
  ) {
    try {
      const { data, error } = await SupabaseInstance.invokeFunction<
        UpdatePasswordFlowParams
      >(
        "update-password",
        { access_token, refresh_token },
      );
      if (error) {
        return {
          response: null,
          error: error?.message || "Failed to get response",
        };
      }
      const { data: session, error: sessionError } = await SupabaseInstance
        .refreshSession(refresh_token);
      return {
        updatePassword: data,
        session,
        sessionError,
        error: null,
      };
    } catch (err) {
      return { response: null, error: `Error setUpdatePasswordFlow: ${err}` };
    }
  }
}

/** Singleton instance of {@link AuthServices}. */
export default new AuthServices();
